import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { trpc } from "@/lib/trpc";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  REMINDER_DELIVERY_POLL_MAX_MS,
  REMINDER_DELIVERY_POLL_MS,
  classifyReminderDeliveryPoll,
  isNeutralPendingReminderOutcome,
  staffReminderToastMessage,
} from "@/components/finance/staffInvoiceReminderFeedback";

function newClientOccurrenceId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID().replace(/-/g, "").slice(0, 32);
  }
  return `occ${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;
}

type Props = {
  open: boolean;
  invoiceId: number | null;
  onOpenChange: (open: boolean) => void;
  onSettled?: () => void;
};

/**
 * Staff Confirm + Send reminder modal.
 * Explicit Email/WhatsApp only (no auto). Uses salesDeals.sendInvoiceReminder.
 */
export function SendInvoiceReminderDialog({
  open,
  invoiceId,
  onOpenChange,
  onSettled,
}: Props) {
  const utils = trpc.useUtils();
  const [channel, setChannel] = useState<"email" | "whatsapp" | "">("");
  const [clientOccurrenceId, setClientOccurrenceId] = useState(() =>
    newClientOccurrenceId()
  );
  const [lastOutcome, setLastOutcome] = useState<string | null>(null);
  const [pendingDeliveryLogId, setPendingDeliveryLogId] = useState<number | null>(
    null
  );
  const [pollStartedAt, setPollStartedAt] = useState<number | null>(null);

  useEffect(() => {
    if (open && invoiceId) {
      setClientOccurrenceId(newClientOccurrenceId());
      setChannel("");
      setLastOutcome(null);
      setPendingDeliveryLogId(null);
      setPollStartedAt(null);
    }
  }, [open, invoiceId]);

  useEffect(() => {
    if (!open) {
      setPendingDeliveryLogId(null);
      setPollStartedAt(null);
    }
  }, [open]);

  const previewQuery = trpc.salesDeals.previewInvoiceReminder.useQuery(
    { invoiceId: invoiceId ?? 0 },
    { enabled: open && Boolean(invoiceId), retry: false }
  );

  const deliveryStatusQuery = trpc.notifications.getDeliveryLogStatus.useQuery(
    { deliveryLogId: pendingDeliveryLogId ?? 0 },
    {
      enabled: open && pendingDeliveryLogId != null && pendingDeliveryLogId > 0,
      refetchInterval: query => {
        if (!open || pendingDeliveryLogId == null) return false;
        const started = pollStartedAt ?? Date.now();
        if (Date.now() - started > REMINDER_DELIVERY_POLL_MAX_MS) return false;
        const classified = classifyReminderDeliveryPoll({
          status: query.state.data?.status,
          providerStatus: query.state.data?.providerStatus,
        });
        if (classified !== "pending") return false;
        return REMINDER_DELIVERY_POLL_MS;
      },
    }
  );

  useEffect(() => {
    if (!pendingDeliveryLogId || !deliveryStatusQuery.data) return;
    const classified = classifyReminderDeliveryPoll({
      status: deliveryStatusQuery.data.status,
      providerStatus: deliveryStatusQuery.data.providerStatus,
    });
    if (classified === "pending") return;
    const feedback = staffReminderToastMessage(classified, {
      channel: deliveryStatusQuery.data.channel ?? "whatsapp",
      reason:
        deliveryStatusQuery.data.errorMessage ||
        deliveryStatusQuery.data.providerErrorMessage,
    });
    setLastOutcome(classified);
    if (feedback.kind === "success") toast.success(feedback.text);
    else if (feedback.kind === "error") toast.error(feedback.text);
    else toast.message(feedback.text);
    setPendingDeliveryLogId(null);
    setPollStartedAt(null);
    void Promise.all([
      utils.financeInvoices.invalidate(),
      utils.salesDeals.invalidate(),
      utils.proposals.invalidate(),
      utils.notifications.invalidate(),
      utils.signals.invalidate(),
      utils.revenueSchedule.invalidate(),
    ]);
    onSettled?.();
    if (classified === "sent" || classified === "delivered" || classified === "read") {
      onOpenChange(false);
    }
  }, [
    pendingDeliveryLogId,
    deliveryStatusQuery.data,
    onOpenChange,
    onSettled,
    utils.financeInvoices,
    utils.notifications,
    utils.proposals,
    utils.revenueSchedule,
    utils.salesDeals,
    utils.signals,
  ]);

  const sendMutation = trpc.salesDeals.sendInvoiceReminder.useMutation({
    onSuccess: async result => {
      setLastOutcome(result.outcome);
      const feedback = staffReminderToastMessage(result.outcome, {
        channel: "channel" in result ? result.channel : channel || "whatsapp",
        reason: "reason" in result ? result.reason : null,
      });
      if (feedback.kind === "success") toast.success(feedback.text);
      else if (feedback.kind === "error") toast.error(feedback.text);
      else toast.message(feedback.text);

      const deliveryLogId =
        "deliveryLogId" in result && typeof result.deliveryLogId === "number"
          ? result.deliveryLogId
          : null;
      const shouldPoll =
        (result.outcome === "submitted" || result.outcome === "already_submitted") &&
        deliveryLogId != null;

      await Promise.all([
        utils.financeInvoices.invalidate(),
        utils.salesDeals.invalidate(),
        utils.proposals.invalidate(),
        utils.notifications.invalidate(),
        utils.signals.invalidate(),
        utils.revenueSchedule.invalidate(),
      ]);
      onSettled?.();

      if (shouldPoll) {
        setPendingDeliveryLogId(deliveryLogId);
        setPollStartedAt(Date.now());
        return;
      }

      if (
        result.outcome === "sent" ||
        result.outcome === "already_sent" ||
        result.outcome === "skipped"
      ) {
        onOpenChange(false);
      }
    },
    onError: err => {
      toast.error(err.message || "Could not send reminder");
      setLastOutcome("failed");
    },
  });

  const preview = previewQuery.data;
  const emailOk = Boolean(preview?.email?.trim());
  const phoneOk = Boolean(preview?.phone?.trim());

  useEffect(() => {
    if (!preview) return;
    if (!channel) {
      if (emailOk) setChannel("email");
      else if (phoneOk) setChannel("whatsapp");
    }
  }, [preview, emailOk, phoneOk, channel]);

  const canConfirm = Boolean(
    invoiceId && channel && (channel === "email" ? emailOk : phoneOk)
  );

  const recipient = useMemo(() => {
    if (!preview || !channel) return "—";
    return channel === "email" ? preview.email : preview.phone;
  }, [preview, channel]);

  const pendingFeedback =
    lastOutcome && isNeutralPendingReminderOutcome(lastOutcome)
      ? staffReminderToastMessage(lastOutcome, { channel: channel || "whatsapp" }).text
      : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Send invoice reminder</DialogTitle>
          <DialogDescription>
            Review the recipient and message, then confirm. The server
            revalidates that the invoice is still outstanding before sending.
          </DialogDescription>
        </DialogHeader>

        {previewQuery.isLoading ? (
          <p className="text-sm text-muted-foreground">
            Loading reminder preview…
          </p>
        ) : previewQuery.error ? (
          <p className="text-sm text-destructive">
            {previewQuery.error.message}
          </p>
        ) : preview ? (
          <div className="space-y-3 text-sm">
            <div>
              <div className="font-medium">
                {preview.customerName ?? "Customer"}
              </div>
              <div className="text-muted-foreground">
                {preview.invoiceNumber} · {preview.amount} outstanding
                {preview.proposalRef ? ` · ${preview.proposalRef}` : ""}
              </div>
              <div className="text-muted-foreground">
                Status: {preview.invoiceStatus}
              </div>
            </div>

            <div className="space-y-2">
              <Label>Channel</Label>
              <RadioGroup
                value={channel}
                onValueChange={v => setChannel(v as "email" | "whatsapp")}
                className="gap-2"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem
                    value="email"
                    id="reminder-email"
                    disabled={!emailOk}
                  />
                  <Label
                    htmlFor="reminder-email"
                    className={!emailOk ? "opacity-50" : ""}
                  >
                    Email {emailOk ? `(${preview.email})` : "(not on file)"}
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem
                    value="whatsapp"
                    id="reminder-wa"
                    disabled={!phoneOk}
                  />
                  <Label
                    htmlFor="reminder-wa"
                    className={!phoneOk ? "opacity-50" : ""}
                  >
                    WhatsApp {phoneOk ? `(${preview.phone})` : "(not on file)"}
                  </Label>
                </div>
              </RadioGroup>
            </div>

            <div>
              <div className="text-muted-foreground">Recipient</div>
              <div className="font-medium break-all">{recipient}</div>
            </div>

            <div className="rounded-md border p-3 bg-muted/30 whitespace-pre-wrap">
              {channel === "email" ? (
                <>
                  <div className="font-medium mb-1">
                    {preview.messageEmailSubject}
                  </div>
                  {preview.messageBody}
                </>
              ) : (
                preview.messageBody
              )}
            </div>
            <div className="text-xs text-muted-foreground break-all">
              Attachment / link: {preview.invoiceUrl || "Invoice link"}
            </div>

            {pendingFeedback ? (
              <div className="text-xs text-muted-foreground">{pendingFeedback}</div>
            ) : lastOutcome ? (
              <div className="text-xs text-muted-foreground">
                Last outcome: {lastOutcome}
              </div>
            ) : null}
          </div>
        ) : null}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            disabled={!canConfirm || sendMutation.isPending || !preview}
            onClick={() => {
              if (!invoiceId || (channel !== "email" && channel !== "whatsapp"))
                return;
              sendMutation.mutate({
                invoiceId,
                channel,
                clientOccurrenceId,
                confirmed: true,
              });
            }}
          >
            {sendMutation.isPending ? "Sending…" : "Confirm Send"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
